import { ChevronDown, Play, RotateCcw, Sparkles, Trash2, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { BlockedAction } from '@/components/custom-ui/BlockedAction'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AUTO_PIPELINE_ID,
  AUTO_PIPELINE_LABEL,
  type Phase2Workflow,
} from '@/features/documents/types'

export interface SelectionBarProps {
  /** `captureCount`: selected HUMINT captures, which block run, reset and delete (ADR-0045). */
  selection: { folderCount: number; fileCount: number; totalItems: number; captureCount?: number }
  extraction: {
    isLoading: boolean
    pipelines: Phase2Workflow[]
    selectedPipelineId: string | null
    onPipelineSelect: (id: string) => void
    onRun: () => void
  }
  actions: { onClear: () => void; onReset: () => void; onDelete: () => void }
  schemaSetup?: ReactNode
}

export function SelectionBar({ selection, extraction, actions, schemaSetup }: SelectionBarProps) {
  const { t } = useTranslation()
  const captureCount = selection.captureCount ?? 0
  const locked =
    captureCount > 0 ? t('documents.capture.selectionLocked', { count: captureCount }) : null
  // Phone: two compact rows — summary + reset/delete + clear on the first; the
  // second child (schema prompt, or pipeline and Run) moves to a row of its own.
  return (
    <Card className="flex flex-row flex-wrap items-stretch gap-3 p-3 shadow-lg max-md:gap-2 max-md:p-2">
      <SelectionSummary selection={selection} />
      {schemaSetup ? (
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 max-md:order-last max-md:basis-full max-md:gap-2 max-md:px-1 max-md:[&>a]:flex-1 max-md:[&>button]:flex-1">
          {schemaSetup}
        </div>
      ) : (
        <ExtractionControls extraction={extraction} locked={locked} />
      )}
      <SelectionActions actions={actions} showReset={!schemaSetup} locked={locked} />
      <Button
        variant="ghost"
        size="icon-sm"
        className="w-10 rounded-xl border border-border bg-muted hover:bg-accent"
        onClick={actions.onClear}
        aria-label="Clear selection"
      >
        <X className="size-4" />
      </Button>
    </Card>
  )
}

function SelectionSummary({ selection }: Pick<SelectionBarProps, 'selection'>) {
  const { totalItems, folderCount, fileCount } = selection
  return (
    <div className="inline-flex items-center gap-3 rounded-xl border border-border bg-muted px-4 py-2 max-md:min-w-0 max-md:flex-1 max-md:px-3 max-md:py-1">
      <div className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary max-md:hidden">
        <Sparkles className="size-4" aria-hidden />
      </div>
      <div className="flex flex-col leading-tight max-md:min-w-0">
        <span className="text-sm font-semibold text-foreground max-md:truncate">
          {totalItems} {totalItems === 1 ? 'item' : 'items'} selected
        </span>
        {folderCount > 0 && fileCount > 0 && (
          <span className="text-2xs text-muted-foreground max-md:truncate">
            {folderCount} {folderCount === 1 ? 'folder' : 'folders'} · {fileCount}{' '}
            {fileCount === 1 ? 'file' : 'files'}
          </span>
        )}
      </div>
    </div>
  )
}

/** Why the selection's actions are blocked (it holds captures); null when they are not. */
type LockedReason = string | null

function ExtractionControls({
  extraction,
  locked,
}: Pick<SelectionBarProps, 'extraction'> & { locked: LockedReason }) {
  return (
    <div className="ml-auto flex flex-wrap items-stretch gap-3 max-md:order-last max-md:basis-full max-md:flex-nowrap max-md:gap-2 max-md:[&>*:last-child]:flex-1">
      <PipelineSelector extraction={extraction} />
      <BlockedAction reason={locked}>
        <Button
          size="sm"
          className="h-auto px-5 rounded-xl text-sm font-semibold gap-2 max-md:min-h-11 max-md:flex-1 max-md:px-3"
          onClick={extraction.onRun}
          disabled={extraction.isLoading || locked !== null}
        >
          <Play className="size-4" aria-hidden />
          {extraction.isLoading ? 'Running…' : 'Run Extraction'}
        </Button>
      </BlockedAction>
    </div>
  )
}

function PipelineSelector({ extraction }: Pick<SelectionBarProps, 'extraction'>) {
  const { selectedPipelineId, pipelines, onPipelineSelect } = extraction
  const isAuto = selectedPipelineId === AUTO_PIPELINE_ID
  const selectedExplicit = pipelines.find(p => p.id === selectedPipelineId)
  const triggerLabel = isAuto ? AUTO_PIPELINE_LABEL : (selectedExplicit?.name ?? 'Select pipeline')
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="h-auto justify-between gap-3 rounded-xl px-4 py-2 max-md:px-3 max-md:py-1"
        >
          <span className="flex min-w-0 flex-col items-start leading-tight">
            <span className="text-xs text-muted-foreground">Pipeline</span>
            <span className="text-sm font-medium text-foreground truncate">{triggerLabel}</span>
          </span>
          <ChevronDown className="size-3.5 text-muted-foreground shrink-0" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top">
        <DropdownMenuItem onClick={() => onPipelineSelect(AUTO_PIPELINE_ID)}>
          <span className="size-2 rounded-full mr-2 shrink-0 bg-primary" />
          <span className="flex-1">{AUTO_PIPELINE_LABEL}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SelectionActions({
  actions,
  showReset,
  locked,
}: Pick<SelectionBarProps, 'actions'> & { showReset: boolean; locked: LockedReason }) {
  return (
    <BlockedAction reason={locked}>
      <div className="inline-flex items-center gap-1 px-2 rounded-xl border border-border bg-muted max-md:px-1">
        {showReset && (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-xs max-md:size-9 max-md:px-0"
              onClick={actions.onReset}
              disabled={locked !== null}
            >
              <RotateCcw className="size-3.5" aria-hidden />
              <span className="max-md:sr-only">Reset</span>
            </Button>
            <span className="w-px h-5 bg-border" />
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-xs text-destructive hover:bg-destructive/10 max-md:size-9 max-md:px-0"
          onClick={actions.onDelete}
          disabled={locked !== null}
        >
          <Trash2 className="size-3.5" aria-hidden />
          <span className="max-md:sr-only">Delete</span>
        </Button>
      </div>
    </BlockedAction>
  )
}
